import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import { Order } from "@/redux/services/order/order.type";
import { PaymentMethodName } from "@/redux/services/pos/pos.type";
import { useAddPaymentMutation } from "@/redux/services/pos/payment.api";
import { useRestaurantCurrency } from "@/lib/hooks";
import { cn, convertPriceToCent, displayPrice } from "@/lib/utils";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/apiErrors";
import PaymentMethodGrid from "./PaymentMethodGrid";
import AmountInput from "./AmountInput";
import CashChangeDisplay from "./CashChangeDisplay";
import ReceiptPreview from "@/components/pos/receipt/ReceiptPreview";
import { useAppDispatch } from "@/redux/features/hooks";
import { OrderApi } from "@/redux/services/order/order.api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
}

// Steps (in main currency units, e.g. XOF) used to build cash-tendered suggestions.
const CASH_SUGGESTION_STEPS = [1000, 5000, 10000];

const roundUpStrict = (value: number, step: number) => {
  const mult = Math.ceil(value / step) * step;
  return mult > value ? mult : mult + step;
};

function buildCashSuggestions(amountInCents: number): number[] {
  const amountInUnits = amountInCents / 100;
  if (amountInUnits <= 0) return [];
  const suggestions = new Set<number>();
  for (const step of CASH_SUGGESTION_STEPS) {
    suggestions.add(roundUpStrict(amountInUnits, step));
  }
  return Array.from(suggestions).sort((a, b) => a - b);
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  order,
}) => {
  const currency = useRestaurantCurrency();
  const dispatch = useAppDispatch();
  const [addPayment, { isLoading }] = useAddPaymentMutation();

  const initialRemaining = order.remainingAmount ?? order.totalPrice;

  const [selectedMethod, setSelectedMethod] =
    useState<PaymentMethodName | null>(null);
  const [amount, setAmount] = useState<number>(initialRemaining);
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [cashTendered, setCashTenderedState] = useState<number>(initialRemaining);
  const cashTenderedTouched = useRef(false);
  const [mobileProvider, setMobileProvider] = useState<string>("");
  const [transactionReference, setTransactionReference] = useState<string>("");
  const [currentRemaining, setCurrentRemaining] =
    useState<number>(initialRemaining);
  const [showReceipt, setShowReceipt] = useState(false);

  // A cash tip is handed over on top of the sale, so the cash the customer owes
  // is amount + tip. Suggestions, the "exact" shortcut and the default tendered
  // all track this total.
  const cashDue = amount + tipAmount;
  const cashSuggestions = useMemo(
    () => buildCashSuggestions(cashDue),
    [cashDue]
  );

  // Keep tendered amount synced to the cash due until the cashier overrides it.
  useEffect(() => {
    if (!cashTenderedTouched.current) {
      setCashTenderedState(cashDue);
    }
  }, [cashDue]);

  const setCashTendered = (value: number) => {
    cashTenderedTouched.current = true;
    setCashTenderedState(value);
  };

  const handleExactChange = () => {
    cashTenderedTouched.current = false;
    setCashTenderedState(cashDue);
  };

  const resetForm = () => {
    setSelectedMethod(null);
    setTipAmount(0);
    cashTenderedTouched.current = false;
    setCashTenderedState(amount);
    setMobileProvider("");
    setTransactionReference("");
  };

  const handleClose = () => {
    setAmount(initialRemaining);
    setTipAmount(0);
    setCurrentRemaining(initialRemaining);
    setShowReceipt(false);
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedMethod) {
      toast.error("Veuillez choisir un moyen de paiement");
      return;
    }

    if (amount <= 0) {
      toast.error("Veuillez saisir un montant valide");
      return;
    }

    if (selectedMethod === "mobile money" && !mobileProvider) {
      toast.error("Veuillez sélectionner un fournisseur");
      return;
    }

    try {
      const paymentData: {
        paymentMethod: PaymentMethodName;
        amount: number;
        tipAmount?: number;
        cashTendered?: number;
        transactionReference?: string;
        metadata?: { provider: string };
      } = {
        paymentMethod: selectedMethod,
        amount,
      };

      if (tipAmount > 0) {
        paymentData.tipAmount = tipAmount;
      }

      if (selectedMethod === "cash" && cashTendered > 0) {
        paymentData.cashTendered = cashTendered;
      }

      if (selectedMethod === "mobile money") {
        paymentData.metadata = { provider: mobileProvider };
        if (transactionReference) {
          paymentData.transactionReference = transactionReference;
        }
      }

      await addPayment({
        orderUuid: order.id,
        data: paymentData,
      }).unwrap();

      dispatch(OrderApi.util.invalidateTags(["order-detail"]));

      const newRemaining = currentRemaining - amount;

      if (newRemaining <= 0) {
        toast.success("Paiement complet enregistré");
        setShowReceipt(true);
      } else {
        setCurrentRemaining(newRemaining);
        setAmount(newRemaining);
        resetForm();
        toast.success("Paiement enregistré. Montant restant mis à jour.");
      }
    } catch (error) {
      toastApiError(error, "Erreur lors de l'enregistrement du paiement");
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen && !showReceipt}
      onClose={handleClose}
      title="Paiement"
      size="medium"
    >
      <div className="flex flex-col gap-4">
        {/* Remaining amount banner */}
        <div className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-3">
          <span className="text-sm text-muted-foreground">Restant à payer</span>
          <span className="text-base font-semibold text-card-foreground">
            {displayPrice(currentRemaining, currency)}
          </span>
        </div>

        {/* Payment method */}
        <div>
          <p className="text-sm font-medium text-card-foreground mb-2">
            Moyen de paiement
          </p>
          <PaymentMethodGrid
            selectedMethod={selectedMethod}
            onSelect={setSelectedMethod}
          />
        </div>

        {/* Amount */}
        <AmountInput
          value={amount}
          onChange={setAmount}
          label="Montant"
          placeholder="Saisir le montant"
        />

        {/* Tip — on top of the order total, applies to any payment method */}
        <AmountInput
          value={tipAmount}
          onChange={setTipAmount}
          label="Pourboire (optionnel)"
          placeholder="Montant du pourboire"
        />

        {/* Cash-specific fields */}
        {selectedMethod === "cash" && (
          <div className="flex flex-col gap-2">
            <AmountInput
              value={cashTendered}
              onChange={setCashTendered}
              label="Espèces reçues"
              placeholder="Montant donné par le client"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleExactChange}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                  !cashTenderedTouched.current
                    ? "bg-primary text-white border-primary"
                    : "bg-muted text-muted-foreground border-border hover:bg-border"
                )}
              >
                Montant exact
              </button>
              {cashSuggestions.map((denom) => {
                const valueInCents = convertPriceToCent(denom);
                const isActive =
                  cashTenderedTouched.current && cashTendered === valueInCents;
                return (
                  <button
                    key={denom}
                    type="button"
                    onClick={() => setCashTendered(valueInCents)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                      isActive
                        ? "bg-primary text-white border-primary"
                        : "bg-muted text-muted-foreground border-border hover:bg-border"
                    )}
                  >
                    {displayPrice(valueInCents, currency)}
                  </button>
                );
              })}
            </div>
            <CashChangeDisplay
              cashTendered={cashTendered}
              amount={amount}
              tipAmount={tipAmount}
              currency={currency}
            />
          </div>
        )}

        {/* Mobile money fields */}
        {selectedMethod === "mobile money" && (
          <>
            <div>
              <p className="text-sm font-medium text-card-foreground mb-2">
                Fournisseur
              </p>
              <Select
                value={mobileProvider}
                onValueChange={setMobileProvider}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un fournisseur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Orange Money">Orange Money</SelectItem>
                  <SelectItem value="Ecobank">Ecobank</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-sm font-medium text-card-foreground mb-2">
                Référence transaction (optionnel)
              </p>
              <input
                type="text"
                value={transactionReference}
                onChange={(e) => setTransactionReference(e.target.value)}
                placeholder="Ex: OM-2026-04-07-001"
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </>
        )}

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || !selectedMethod || amount <= 0}
          isLoading={isLoading}
          className="w-full"
        >
          Confirmer le paiement
        </Button>
      </div>
    </Modal>

    <ReceiptPreview
      isOpen={showReceipt}
      onClose={handleClose}
      orderId={order.id}
    />
    </>
  );
};

export default PaymentModal;
